import React, { useState } from 'react';
import style from '../content-type.module.scss';
import { Markdown as TMarkdown  } from '../../types';
import moment from 'moment';
import { EditIcon, DeleteIcon } from '../../../../icons';
import { useMutate, useAppAndBranchId, usePermissions } from '../../../../hooks';
import { api } from '../../api';
import { Modal } from '../../../../components';
import { AddMarkdown } from './AddMarkdown';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import { errorMessage } from '../../../../utils/error';

type Props = {
	markdown: TMarkdown & { id: string; updatedAt: string|null; createdAt: string };
	refetchContent: () => void;
};

export function Markdown(props: Props) {
  const { ErrorToast } = useToast()
	const [appId, branchId] = useAppAndBranchId();
	const { markdown, refetchContent } = props;
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteContent,
	);

	const [modalVisibility, setModalVisibility] = useState({
		editContent: false,
		viewContent: false,
		deleteContent: false,
	});

	const [canEdit, canDelete] = usePermissions(['content.Edit', 'content.Delete']) as boolean[]

	function updateVisibility(
		key: keyof typeof modalVisibility,
		visibility: boolean,
	) {
		setModalVisibility((prev) => ({
			...prev,
			[key]: visibility,
		}));
	}

	return (
		<div className={style.contentTypeWrapper}>
			<div className={style.contentType} data-has-action={canEdit || canDelete} onClick={() => {
					updateVisibility('viewContent', true)
				}}>
				<div className={style.left}>
					<p>{markdown.contentValue.title}</p>
				</div>
				<div className={style.info}>
					<p>{markdown?.updatedAt ? moment(markdown?.updatedAt).format('DD MMM YYYY, hh:mm A') : "" }</p>
				</div>
				<div className={style.info}>
					<p>{moment(markdown.createdAt).format('DD MMM YYYY, hh:mm A')}</p>
					<div className={style.actions}>
						{(canEdit || canDelete) && <div className={style.line} />}
						{canEdit && <button
							onClick={(e) => {
								e.stopPropagation()
								updateVisibility('editContent', true);
							}}
						>
							<EditIcon width="14" height="14" stroke="#141B34" />
						</button>
						}{canDelete &&
							<button
								onClick={async (e) => {
									e.stopPropagation()
									updateVisibility('deleteContent', true);
								}}
							>
								<DeleteIcon width="14" height="14" stroke="#D30000" />
							</button>
						}
					</div>
				</div>
			</div>
			{modalVisibility.viewContent && (
				<AddMarkdown
					mode="view"
					refetchContent={refetchContent}
					initialValue={{
						title: markdown.contentValue.title,
						markdownText: markdown.contentValue.markdownText,
						id: markdown.id,
					}}
					isOpen={modalVisibility.viewContent}
					close={() => {
						updateVisibility('viewContent', false);
					}}
				/>
			)}
			{modalVisibility.editContent && (
				<AddMarkdown
					mode="edit"
					refetchContent={refetchContent}
					initialValue={{
						title: markdown.contentValue.title,
						markdownText: markdown.contentValue.markdownText,
						id: markdown.id,
					}}
					isOpen={modalVisibility.editContent}
					close={() => updateVisibility('editContent', false)}
				/>
			)}
			<Modal
				styles={{
					'--ok-btn-bg': '#DE0000',
					'--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
				}}
				okText="Delete"
				loading={deleteLoading}
				onOk={async () => {
					try {
						await deleteMutate(appId, branchId, markdown.id);
						refetchContent();
					} catch(err) {
					  ErrorToast(errorMessage("delete", "markdown", err?.code))
					}
				}}
				onCancel={() => updateVisibility('deleteContent', false)}
				isOpen={modalVisibility.deleteContent}
				title="Remove Chosen Markdown"
			>
				<div
					style={{
						padding: '18px 14px',
						color: '#393939',
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					<p>Do you really want to remove the chosen markdown?</p>
				</div>
			</Modal>
		</div>
	);
}
